import axios from 'axios';
import { isOnline } from '@/utils/connectivity';
import { cachePosSnapshot, getCachedPosSnapshot } from '@/utils/posOffline/db';

const CACHE_KEY = 'catalogo_maestro';

/**
 * Guarda snapshot del catálogo compartido para lookup offline por código.
 * @param {Array} items
 */
export async function cacheCatalogoMaestro(items) {
  const list = Array.isArray(items) ? items : [];
  const byCodigo = {};
  for (const it of list) {
    const code = it && it.codigo_barras != null ? String(it.codigo_barras).trim() : '';
    if (!code) continue;
    byCodigo[code] = {
      catalogo_id: it.catalogo_id,
      codigo_barras: code,
      nombre: it.nombre,
      categoria: it.categoria,
      precio_sugerido: it.precio_sugerido,
    };
  }
  await cachePosSnapshot(CACHE_KEY, {
    byCodigo,
    count: Object.keys(byCodigo).length,
    cached_at: new Date().toISOString(),
  });
}

export async function getCachedCatalogoByCodigo(codigo) {
  const code = String(codigo || '').trim();
  if (!code) return null;
  const snap = await getCachedPosSnapshot(CACHE_KEY);
  if (!snap || !snap.byCodigo) return null;
  return snap.byCodigo[code] || null;
}

/** Precarga el catálogo maestro (online). No falla el warm si el endpoint no existe. */
export async function warmCatalogoMaestro() {
  if (!isOnline()) return;
  try {
    const { data } = await axios.get('/catalogo');
    const items = (data && data.items) || [];
    await cacheCatalogoMaestro(items);
  } catch (_) {
    /* catálogo opcional hasta migrar/seed */
  }
}

/**
 * Busca en catálogo por código: API online o caché offline.
 * @returns {Promise<{item: object|null, fromCache: boolean}>}
 */
export async function buscarCatalogoPorCodigo(codigo) {
  const code = String(codigo || '').trim();
  if (!code) return { item: null, fromCache: false };

  if (isOnline()) {
    try {
      const { data } = await axios.get('/catalogo/buscar', {
        params: { codigo: code },
      });
      const item = (data && (data.item || (data.items && data.items[0]))) || null;
      return { item, fromCache: false };
    } catch (_) {
      const cached = await getCachedCatalogoByCodigo(code);
      return { item: cached, fromCache: true };
    }
  }

  const cached = await getCachedCatalogoByCodigo(code);
  return { item: cached, fromCache: true };
}

/**
 * Adopta un ítem del catálogo al inventario del negocio (requiere online).
 */
export async function adoptarDesdeCatalogo({ catalogoId, codigoBarras, usuarioNegocio } = {}) {
  if (!isOnline()) {
    const err = new Error('Sin conexión: no se puede adoptar del catálogo.');
    err.code = 'OFFLINE';
    throw err;
  }
  const body = {};
  if (catalogoId != null) body.catalogo_id = catalogoId;
  if (codigoBarras) body.codigo_barras = codigoBarras;
  if (usuarioNegocio) body.usuario_negocio = usuarioNegocio;
  const { data } = await axios.post('/catalogo/adoptar', body);
  return data;
}
